import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@medical/shared/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { calculateAge, formatAge, getMsUntilNextMonth } from '@medical/shared/utils/ageUtils';
import { translateName, translateNameSync } from '@medical/shared/utils/translateName';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
  text-align: center;
`;

const ProfileCloseButton = styled.button`
  background: #f3f4f6;
  border: none;
  border-radius: 0.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  color: #6b7280;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 1.25rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ProfileBanner = styled.div`
  background: #0B63C5;
  padding: 3rem 2rem;
  text-align: center;
  position: relative;
`;

const AvatarCircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: white;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  font-weight: 700;
  color: #0B63C5;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const PatientName = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const PatientId = styled.div`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  color: white;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 0.5px;
`;

const ProfileContent = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  color: #111827;
  font-weight: 500;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 2rem 0;
`;

const EditButton = styled.button`
  background: #0B63C5;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0B63C5;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SaveButton = styled(Button)`
  background: #0B63C5;
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`;

const CancelButton = styled(Button)`
  background: #f3f4f6;
  color: #374151;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const Profile = () => {
  const { t, i18n } = useTranslation();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [currentAge, setCurrentAge] = useState(null);
  const [translatedFirstName, setTranslatedFirstName] = useState('');
  const [translatedLastName, setTranslatedLastName] = useState('');

  // Refresh user from /api/auth/me when Profile mounts (ensures hospitalId and full patient data)
  useEffect(() => {
    if (!user || user.role !== 'patient') return;
    axios.get('/api/auth/me')
      .then((res) => {
        if (res.data?.user) updateUser(res.data.user);
      })
      .catch(() => {});
  }, []);

  // Translate names when user or language changes
  useEffect(() => {
    const translateNames = async () => {
      if (user?.firstName) {
        const first = translateNameSync(user.firstName);
        setTranslatedFirstName(first);
        // Also try async translation for better results
        translateName(user.firstName).then(setTranslatedFirstName);
      } else {
        setTranslatedFirstName('');
      }
      
      if (user?.lastName) {
        const last = translateNameSync(user.lastName);
        setTranslatedLastName(last);
        // Also try async translation for better results
        translateName(user.lastName).then(setTranslatedLastName);
      } else {
        setTranslatedLastName('');
      }
    };

    translateNames();
  }, [user?.firstName, user?.lastName, i18n.language]);

  // Calculate initial age
  useEffect(() => {
    if (user?.dateOfBirth) {
      const age = calculateAge(user.dateOfBirth);
      setCurrentAge(age);
    } else {
      setCurrentAge(null);
    }
  }, [user?.dateOfBirth]);

  // Set up auto-update for age when month changes
  useEffect(() => {
    if (!user?.dateOfBirth) return;

    // Calculate time until next month
    const msUntilNextMonth = getMsUntilNextMonth();
    
    // Set timeout to update age when month changes
    const timeoutId = setTimeout(() => {
      setCurrentAge(calculateAge(user.dateOfBirth));
      
      // Set up interval to check monthly (check every day to catch month changes)
      const intervalId = setInterval(() => {
        const newAge = calculateAge(user.dateOfBirth);
        if (newAge !== currentAge) {
          setCurrentAge(newAge);
        }
      }, 24 * 60 * 60 * 1000); // Check daily

      return () => clearInterval(intervalId);
    }, msUntilNextMonth);

    return () => clearTimeout(timeoutId);
  }, [user?.dateOfBirth, currentAge]);

  const getInitials = () => {
    const first = user?.firstName?.[0] || '';
    const last = user?.lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getAgeDisplay = () => {
    if (currentAge === null) return t('common.notAvailable', { defaultValue: 'N/A' });
    return t('profile.ageYears', { age: currentAge, defaultValue: `${currentAge} years` });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleEdit = () => {
    setEditForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || ''
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put('/api/auth/profile', {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone
      });
      if (response.data?.user) {
        updateUser(response.data.user);
      }
      setIsEditing(false);
      toast.success(t('profile.saved', { defaultValue: 'Profile saved' }));
    } catch (error) {
      toast.error(error.response?.data?.message || t('profile.saveFailed', { defaultValue: 'Could not save profile' }));
    }
  };

  if (!user) {
    return (
      <ProfileContainer>
        <ProfileCard>
          <ProfileContent>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>{t('profile.loading')}</p>
            </div>
          </ProfileContent>
        </ProfileCard>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <ProfileHeader>
        <HeaderContent>
          <Title>{t('profile.title')}</Title>
          <Subtitle>{t('profile.subtitle', { defaultValue: 'Manage your personal information' })}</Subtitle>
        </HeaderContent>
        <ProfileCloseButton onClick={() => navigate('/body-diagram')} aria-label="Close profile">
          ×
        </ProfileCloseButton>
      </ProfileHeader>

      <ProfileCard>
        <ProfileBanner>
          <AvatarCircle>{getInitials()}</AvatarCircle>
          <PatientName>
            {translatedFirstName || user.firstName} {translatedLastName || user.lastName}
          </PatientName>
          <PatientId>🆔 {user.patientId}</PatientId>
          <PatientId style={{ marginTop: '0.25rem', opacity: 0.95 }}>
            🏥 {t('profile.hospitalId', { defaultValue: 'Hospital ID' })}: {user.hospitalId || t('profile.notProvided', { defaultValue: 'Not provided' })}
          </PatientId>
        </ProfileBanner>

        <ProfileContent>
          <Section>
            <SectionTitle>
              👤 {t('profile.sections.personalInfo', { defaultValue: 'Personal Information' })}
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>{t('login.register.firstName.label')}</InfoLabel>
                {isEditing ? <input value={editForm.firstName} onChange={(event) => setEditForm({ ...editForm, firstName: event.target.value })} /> : <InfoValue>{translatedFirstName || user.firstName}</InfoValue>}
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('login.register.lastName.label')}</InfoLabel>
                {isEditing ? <input value={editForm.lastName} onChange={(event) => setEditForm({ ...editForm, lastName: event.target.value })} /> : <InfoValue>{(translatedLastName || user.lastName) || t('profile.notProvided', { defaultValue: 'Not provided' })}</InfoValue>}
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('profile.patientId', { defaultValue: 'Patient ID' })}</InfoLabel>
                <InfoValue>{user.patientId}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('profile.hospitalId', { defaultValue: 'Hospital ID' })}</InfoLabel>
                <InfoValue>{user.hospitalId || t('profile.notProvided', { defaultValue: 'Not provided' })}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('profile.age', { defaultValue: 'Age' })}</InfoLabel>
                <InfoValue>{getAgeDisplay()}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          <Divider />

          <Section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <SectionTitle>
                📞 {t('profile.sections.contactInfo', { defaultValue: 'Contact Information' })}
              </SectionTitle>
            </div>
            
            <InfoGrid>
              <InfoItem>
                <InfoLabel>{t('profile.phoneNumber', { defaultValue: 'Phone Number' })}</InfoLabel>
                {isEditing ? <input value={editForm.phone} onChange={(event) => setEditForm({ ...editForm, phone: event.target.value })} /> : <InfoValue>{user.phone ? user.phone : t('common.notAvailable', { defaultValue: 'N/A' })}</InfoValue>}
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('profile.dateOfBirth', { defaultValue: 'Date of Birth' })}</InfoLabel>
                <InfoValue>{formatDate(user.dateOfBirth)}</InfoValue>
              </InfoItem>
            </InfoGrid>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              {isEditing ? (
                <>
                  <button type="button" onClick={handleSave}>Save</button>
                  <button type="button" onClick={handleCancel}>Cancel</button>
                </>
              ) : (
                <button type="button" onClick={handleEdit}>Edit</button>
              )}
            </div>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>
              📊 {t('profile.sections.accountInfo', { defaultValue: 'Account Information' })}
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>{t('profile.accountStatus', { defaultValue: 'Account Status' })}</InfoLabel>
                <InfoValue style={{ color: user.isActive ? '#10b981' : '#ef4444' }}>
                  {user.isActive ? `✓ ${t('profile.active', { defaultValue: 'Active' })}` : `✗ ${t('profile.inactive', { defaultValue: 'Inactive' })}`}
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('profile.memberSince', { defaultValue: 'Member Since' })}</InfoLabel>
                <InfoValue>{formatDate(user.createdAt)}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('profile.lastUpdated', { defaultValue: 'Last Updated' })}</InfoLabel>
                <InfoValue>{formatDate(user.updatedAt)}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('profile.role', { defaultValue: 'Role' })}</InfoLabel>
                <InfoValue style={{ textTransform: 'capitalize' }}>{t(`profile.roles.${user.role}`, { defaultValue: user.role })}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

        </ProfileContent>
      </ProfileCard>
    </ProfileContainer>
  );
};

export default Profile;

